import { apiBaseUrl } from '@/config/env'
import { SESSION_EXPIRED_EVENT, SESSION_RENEWED_EVENT } from '@/constants/authEvents'
import { NotificationType } from '@/models/enums/app/NotificationType'
import { i18n } from '@/plugins/i18n'
import { addNotification } from '@/services/notifications/NotificationService'
import JwtCookie from '@/services/userAndAuth/JWTCookie'
import axios, { AxiosError, type AxiosInstance } from 'axios'

let isSessionExpirationHandled = false

if (typeof window !== 'undefined') {
  window.addEventListener(SESSION_RENEWED_EVENT, () => {
    isSessionExpirationHandled = false
  })
}

const axiosInstance = axios.create({
  baseURL: apiBaseUrl,
})

axiosInstance.interceptors.request.use(
  (config) => {
    const token = JwtCookie.getToken()

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    config.headers.Accept = 'application/ld+json'

    if (config.method === 'post' && !(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/ld+json'
    } else if (config.method === 'patch') {
      config.headers['Content-Type'] = 'application/merge-patch+json'
    }

    return config
  },
  (error) => Promise.reject(error),
)

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const originalRequest = error.config as any

    const requestUrl = error.config?.url || error.response?.config?.url || ''
    const isAtlasRequest = requestUrl.includes('/api/atlases')

    if (error.response?.status === 404 && isAtlasRequest && error.config?.method === 'get') {
      return Promise.resolve({
        data: { 'hydra:member': [] },
        status: 200,
        statusText: 'OK',
        headers: error.response?.headers || {},
        config: error.config,
      })
    }

    // authentication error handling (Invalid JWT Token, expired session, etc.)
    if (error.response?.status === 401) {
      if (!originalRequest._retry) {
        originalRequest._retry = true

        if (!isSessionExpirationHandled && typeof window !== 'undefined') {
          isSessionExpirationHandled = true
          window.dispatchEvent(
            new CustomEvent(SESSION_EXPIRED_EVENT, {
              detail: {
                redirectPath: window.location.pathname + window.location.search + window.location.hash,
              },
            }),
          )
        }
      }
    }

    // Gestion des autres erreurs
    switch (error.response?.status) {
      case 422:
        addNotification(
          i18n.t('notifications.common.error.411'),
          NotificationType.ERROR,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          `${i18n.t('notifications.common.error.moreDetails')} ${(error.response.data as any)?.description ?? ''}`,
        )
        break
      case 500:
        // Sentry.captureException(error)
        break
    }

    return Promise.reject(error)
  },
)

export const apiClient: AxiosInstance = axiosInstance
